import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth, API } from "@/context/AuthContext";
import { Copy, Check, Sparkles, Linkedin, Instagram, Brain, Loader2, X, FolderOpen, FileText, File } from "lucide-react";
import { toast } from "sonner";

const TONES = [
    { id: "friendly", label: "Friendly" },
    { id: "professional", label: "Professional" },
    { id: "bold", label: "Bold" },
    { id: "consultative", label: "Consultative" },
    { id: "empathetic", label: "Empathetic" },
];

const INDUSTRIES = [
    { id: "saas", label: "SaaS" },
    { id: "real_estate", label: "Real Estate" },
    { id: "coaching", label: "Coaching" },
    { id: "agency", label: "Agency" },
    { id: "ecommerce", label: "E-commerce" },
    { id: "fitness", label: "Fitness" },
    { id: "finance", label: "Finance" },
    { id: "home_improvement", label: "Home Improvement" },
    { id: "insurance", label: "Insurance" },
    { id: "stem_cell_clinics", label: "Stem Cell Clinics" },
    { id: "medical_coaches", label: "Medical Coaches" },
];

const GOALS = [
    { id: "book_meeting", label: "Book meeting" },
    { id: "qualify", label: "Qualify" },
    { id: "nurture", label: "Nurture" },
];

const Pill = ({ active, onClick, children, testid }) => (
    <button
        type="button"
        onClick={onClick}
        data-testid={testid}
        className={`px-3.5 py-2 text-sm rounded-md border transition-all ${
            active
                ? "border-signal bg-signal/10 text-white"
                : "border-zinc-800 bg-zinc-900/40 text-zinc-400 hover:border-zinc-700 hover:text-white"
        }`}
    >
        {children}
    </button>
);

export default function NewConversation() {
    const { authHeaders } = useAuth();
    const navigate = useNavigate();
    const [platform, setPlatform] = useState("linkedin");
    const [conversation, setConversation] = useState("");
    const [tone, setTone] = useState("consultative");
    const [industry, setIndustry] = useState("saas");
    const [goal, setGoal] = useState("book_meeting");
    const [context, setContext] = useState("");
    const [loading, setLoading] = useState(false);
    const [variants, setVariants] = useState([]);
    const [copied, setCopied] = useState(null);

    // Coach mode (critique)
    const [critique, setCritique] = useState(null);
    const [critiquing, setCritiquing] = useState(false);

    // Product Library
    const [libraryDocs, setLibraryDocs] = useState([]);
    const [selectedDocIds, setSelectedDocIds] = useState([]);

    useEffect(() => {
        (async () => {
            try {
                const res = await axios.get(`${API}/outreach/documents`, { headers: authHeaders() });
                setLibraryDocs(res.data || []);
            } catch { /* ignore — library is optional */ }
        })(); // eslint-disable-next-line
    }, []);

    const toggleDoc = (id) => {
        setSelectedDocIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
    };

    const generate = async () => {
        if (!conversation.trim()) {
            toast.error("Paste the conversation first.");
            return;
        }
        setLoading(true);
        setVariants([]);
        try {
            const res = await axios.post(
                `${API}/conversations/generate`,
                {
                    platform, conversation, tone, industry, goal, context,
                    document_ids: selectedDocIds.length > 0 ? selectedDocIds : null,
                    save: true,
                },
                { headers: authHeaders() }
            );
            setVariants(res.data.variants);
            toast.success("3 replies drafted — opening your chat…");
            if (res.data.conversation_id) {
                setTimeout(() => navigate(`/app/chat/${res.data.conversation_id}`), 300);
            }
        } catch (err) {
            const status = err?.response?.status;
            if (status === 402) {
                toast.error(err?.response?.data?.detail || "Free plan limit reached. Upgrade to Pro.", { duration: 8000 });
            } else {
                toast.error(err?.response?.data?.detail || "Generation failed");
            }
        } finally {
            setLoading(false);
        }
    };

    const copyVariant = async (v) => {
        try {
            await navigator.clipboard.writeText(v.text);
            setCopied(v.id);
            toast.success("Copied to clipboard");
            setTimeout(() => setCopied(null), 1500);
        } catch {
            toast.error("Copy failed — select and copy manually.");
        }
    };

    const runCritique = async () => {
        if (!conversation.trim()) {
            toast.error("Paste the conversation first.");
            return;
        }
        setCritiquing(true);
        setCritique(null);
        try {
            const res = await axios.post(
                `${API}/conversations/critique`,
                {
                    conversation, tone, industry, platform,
                    document_ids: selectedDocIds.length > 0 ? selectedDocIds : null,
                },
                { headers: authHeaders() }
            );
            setCritique(res.data);
        } catch (err) {
            const status = err?.response?.status;
            if (status === 402) {
                toast.error(err?.response?.data?.detail || "Free plan limit reached. Upgrade to Pro.");
            } else {
                toast.error(err?.response?.data?.detail || "Coach analysis failed");
            }
        } finally {
            setCritiquing(false);
        }
    };

    return (
        <div data-testid="new-conversation-page">
            <div className="mb-8">
                <div className="text-xs font-mono uppercase tracking-[0.2em] text-signal mb-2">/ NEW REPLY</div>
                <h1 className="font-heading font-black text-4xl md:text-5xl tracking-tight">Draft the next move.</h1>
                <p className="text-zinc-400 mt-2">Paste the thread. Pick the lens. Pick the best variant.</p>
            </div>

            <div className="grid lg:grid-cols-2 gap-6">
                {/* LEFT: INPUT */}
                <div className="border border-zinc-900 bg-[#0c0c0c] p-6 space-y-6" data-testid="input-panel">
                    <div>
                        <label className="block text-xs font-mono uppercase tracking-widest text-zinc-500 mb-3">Platform</label>
                        <div className="flex gap-2">
                            <Pill active={platform === "linkedin"} onClick={() => setPlatform("linkedin")} testid="platform-linkedin">
                                <span className="inline-flex items-center gap-2"><Linkedin className="w-3.5 h-3.5" /> LinkedIn</span>
                            </Pill>
                            <Pill active={platform === "instagram"} onClick={() => setPlatform("instagram")} testid="platform-instagram">
                                <span className="inline-flex items-center gap-2"><Instagram className="w-3.5 h-3.5" /> Instagram</span>
                            </Pill>
                        </div>
                    </div>

                    <div>
                        <label className="block text-xs font-mono uppercase tracking-widest text-zinc-500 mb-3">Conversation thread</label>
                        <textarea
                            data-testid="conversation-textarea"
                            value={conversation}
                            onChange={(e) => setConversation(e.target.value)}
                            rows={10}
                            placeholder={"Them: Saw your post about scaling outbound...\nMe: Thanks! Curious what triggered the question?\nThem: We're trying to decide between in-house vs agency."}
                            className="w-full bg-zinc-900 border border-zinc-800 rounded-md text-white px-4 py-3 text-sm font-mono focus:outline-none focus:ring-1 focus:ring-signal focus:border-signal transition-all resize-none"
                        />
                    </div>

                    <div>
                        <label className="block text-xs font-mono uppercase tracking-widest text-zinc-500 mb-3">Tone</label>
                        <div className="flex flex-wrap gap-2">
                            {TONES.map((t) => (
                                <Pill key={t.id} active={tone === t.id} onClick={() => setTone(t.id)} testid={`tone-${t.id}`}>
                                    {t.label}
                                </Pill>
                            ))}
                        </div>
                    </div>

                    <div>
                        <label className="block text-xs font-mono uppercase tracking-widest text-zinc-500 mb-3">Industry</label>
                        <div className="flex flex-wrap gap-2">
                            {INDUSTRIES.map((i) => (
                                <Pill key={i.id} active={industry === i.id} onClick={() => setIndustry(i.id)} testid={`industry-${i.id}`}>
                                    {i.label}
                                </Pill>
                            ))}
                        </div>
                    </div>

                    <div>
                        <label className="block text-xs font-mono uppercase tracking-widest text-zinc-500 mb-3">Goal</label>
                        <div className="flex flex-wrap gap-2">
                            {GOALS.map((g) => (
                                <Pill key={g.id} active={goal === g.id} onClick={() => setGoal(g.id)} testid={`goal-${g.id}`}>
                                    {g.label}
                                </Pill>
                            ))}
                        </div>
                    </div>

                    <div>
                        <label className="block text-xs font-mono uppercase tracking-widest text-zinc-500 mb-3">
                            Extra context <span className="text-zinc-600 normal-case">(optional)</span>
                        </label>
                        <input
                            data-testid="context-input"
                            value={context}
                            onChange={(e) => setContext(e.target.value)}
                            placeholder="E.g. Prospect is a Series B CRO, my offer is outbound-as-a-service."
                            className="w-full bg-zinc-900 border border-zinc-800 rounded-md text-white px-4 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-signal focus:border-signal"
                        />
                    </div>

                    {libraryDocs.length > 0 && (
                        <div>
                            <label className="block text-xs font-mono uppercase tracking-widest text-zinc-500 mb-3">
                                <span className="inline-flex items-center gap-2"><FolderOpen className="w-3.5 h-3.5" /> Product library</span>
                                <span className="text-zinc-600 normal-case"> · pick docs to feed as context</span>
                            </label>
                            <div className="space-y-1.5" data-testid="library-picker">
                                {libraryDocs.map((d) => {
                                    const ext = (d.filename.split(".").pop() || "").toLowerCase();
                                    const Icon = ext === "pdf" ? FileText : File;
                                    const checked = selectedDocIds.includes(d.id);
                                    return (
                                        <label
                                            key={d.id}
                                            data-testid={`library-pick-${d.id}`}
                                            className={`flex items-center gap-3 border rounded-md px-3 py-2 text-sm cursor-pointer transition-colors ${
                                                checked ? "border-signal/50 bg-signal/5" : "border-zinc-800 bg-zinc-900/40 hover:border-zinc-700"
                                            }`}
                                        >
                                            <input
                                                type="checkbox"
                                                checked={checked}
                                                onChange={() => toggleDoc(d.id)}
                                                className="accent-signal w-4 h-4 flex-shrink-0"
                                                data-testid={`library-pick-toggle-${d.id}`}
                                            />
                                            <Icon className="w-4 h-4 text-signal flex-shrink-0" />
                                            <span className="flex-1 truncate">{d.filename}</span>
                                            <span className="text-[10px] font-mono text-zinc-500 flex-shrink-0">{d.char_count.toLocaleString()} chars</span>
                                        </label>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    <button
                        data-testid="generate-btn"
                        onClick={generate}
                        disabled={loading}
                        className="w-full bg-signal hover:bg-signal-hover text-white px-6 py-3.5 rounded-md font-semibold flex items-center justify-center gap-2 transition-colors disabled:opacity-60"
                    >
                        {loading ? (
                            <span className="flex items-center gap-2"><span className="animate-pulse">Drafting…</span></span>
                        ) : (
                            <><Sparkles className="w-4 h-4" /> Generate 3 variants</>
                        )}
                    </button>
                    <button
                        data-testid="coach-btn"
                        onClick={runCritique}
                        disabled={critiquing}
                        className="w-full border border-zinc-800 hover:border-zinc-600 text-zinc-300 hover:text-white px-6 py-3 rounded-md font-semibold flex items-center justify-center gap-2 transition-colors disabled:opacity-60"
                    >
                        {critiquing ? <><Loader2 className="w-4 h-4 animate-spin" /> Analysing…</> : <><Brain className="w-4 h-4" /> Coach me on my pitch</>}
                    </button>
                </div>

                {/* RIGHT: OUTPUT */}
                <div className="space-y-4" data-testid="output-panel">
                    <div className="text-xs font-mono uppercase tracking-widest text-zinc-500 mb-2">Drafts</div>

                    {loading && (
                        <div className="space-y-4">
                            {[0, 1, 2].map((i) => (
                                <div key={i} className="border border-zinc-900 bg-[#0c0c0c] p-6 tracing-beam">
                                    <div className="h-4 bg-zinc-900 rounded mb-3 w-1/3" />
                                    <div className="h-3 bg-zinc-900 rounded mb-2 w-full" />
                                    <div className="h-3 bg-zinc-900 rounded mb-2 w-11/12" />
                                    <div className="h-3 bg-zinc-900 rounded w-3/4" />
                                </div>
                            ))}
                        </div>
                    )}

                    {!loading && variants.length === 0 && (
                        <div className="border border-zinc-900 border-dashed p-12 text-center text-zinc-500">
                            Your 3 variants will appear here.
                        </div>
                    )}

                    {variants.map((v, idx) => (
                        <div
                            key={v.id}
                            className="border border-zinc-900 bg-[#0c0c0c] p-6 hover:border-zinc-700 transition-colors fade-up"
                            style={{ animationDelay: `${idx * 60}ms` }}
                            data-testid={`variant-${idx}`}
                        >
                            <div className="flex items-start justify-between gap-3 mb-4">
                                <div className="flex items-center gap-2">
                                    <div className="text-xs font-mono uppercase tracking-widest text-signal">Variant {idx + 1}</div>
                                    <div className="text-xs text-zinc-500">· {v.angle}</div>
                                </div>
                                <div
                                    className={`px-2 py-1 rounded text-xs font-mono border ${
                                        v.confidence >= 80
                                            ? "bg-green-500/10 text-green-400 border-green-500/20"
                                            : v.confidence >= 60
                                            ? "bg-yellow-500/10 text-yellow-400 border-yellow-500/20"
                                            : "bg-zinc-800 text-zinc-400 border-zinc-700"
                                    }`}
                                >
                                    {v.confidence}%
                                </div>
                            </div>
                            <p className="text-zinc-100 leading-relaxed whitespace-pre-wrap mb-4">{v.text}</p>
                            <p className="text-xs text-zinc-500 italic mb-4">Why: {v.reasoning}</p>
                            <button
                                data-testid={`copy-variant-${idx}`}
                                onClick={() => copyVariant(v)}
                                className="inline-flex items-center gap-2 border border-zinc-800 hover:border-zinc-600 text-zinc-300 hover:text-white px-4 py-2 rounded-md text-sm transition-colors"
                            >
                                {copied === v.id ? <><Check className="w-3.5 h-3.5" /> Copied</> : <><Copy className="w-3.5 h-3.5" /> Copy</>}
                            </button>
                        </div>
                    ))}
                </div>
            </div>
            {critique && (
                <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-start md:items-center justify-center p-4 overflow-y-auto" onClick={() => setCritique(null)} data-testid="critique-modal">
                    <div className="bg-[#0a0a0a] border border-zinc-800 rounded-md max-w-2xl w-full my-6 relative" onClick={(e) => e.stopPropagation()}>
                        <button onClick={() => setCritique(null)} data-testid="close-critique" className="absolute top-4 right-4 text-zinc-500 hover:text-white">
                            <X className="w-5 h-5" />
                        </button>
                        <div className="p-6 border-b border-zinc-900">
                            <div className="text-xs font-mono uppercase tracking-[0.2em] text-signal mb-1">/ COACH MODE</div>
                            <div className="flex items-center gap-4 mb-2">
                                <h2 className="font-heading font-black text-3xl">Your pitch.</h2>
                                <div className={`font-heading font-black text-5xl ${
                                    critique.grade === "A" ? "text-green-400" :
                                    critique.grade === "B" ? "text-emerald-300" :
                                    critique.grade === "C" ? "text-yellow-400" :
                                    critique.grade === "D" ? "text-orange-400" : "text-red-400"
                                }`}>{critique.grade}</div>
                            </div>
                            <p className="text-zinc-300 text-sm leading-relaxed">{critique.overall}</p>
                        </div>
                        <div className="p-6 space-y-5 max-h-[60vh] overflow-y-auto">
                            {critique.strengths?.length > 0 && (
                                <div>
                                    <div className="text-xs font-mono uppercase tracking-widest text-green-400 mb-2">✓ What worked</div>
                                    <ul className="space-y-1.5 text-sm text-zinc-300">
                                        {critique.strengths.map((s, i) => <li key={i} className="flex gap-2"><span className="text-green-500 shrink-0">·</span>{s}</li>)}
                                    </ul>
                                </div>
                            )}
                            {critique.weaknesses?.length > 0 && (
                                <div>
                                    <div className="text-xs font-mono uppercase tracking-widest text-red-400 mb-2">✗ What hurt you</div>
                                    <ul className="space-y-1.5 text-sm text-zinc-300">
                                        {critique.weaknesses.map((s, i) => <li key={i} className="flex gap-2"><span className="text-red-500 shrink-0">·</span>{s}</li>)}
                                    </ul>
                                </div>
                            )}
                            {critique.missed_opportunities?.length > 0 && (
                                <div>
                                    <div className="text-xs font-mono uppercase tracking-widest text-yellow-400 mb-2">⌐ Missed opportunities</div>
                                    <ul className="space-y-1.5 text-sm text-zinc-300">
                                        {critique.missed_opportunities.map((s, i) => <li key={i} className="flex gap-2"><span className="text-yellow-500 shrink-0">·</span>{s}</li>)}
                                    </ul>
                                </div>
                            )}
                            {critique.nepq_questions?.length > 0 && (
                                <div>
                                    <div className="text-xs font-mono uppercase tracking-widest text-signal mb-2">→ Ask these next time (NEPQ)</div>
                                    <div className="space-y-2">
                                        {critique.nepq_questions.map((q, i) => (
                                            <div key={i} className="flex items-start gap-2 p-3 bg-signal/5 border border-signal/20 rounded-md text-sm text-zinc-100">
                                                <span className="text-signal shrink-0 font-mono">Q{i+1}.</span>
                                                <span className="flex-1">{q}</span>
                                                <button
                                                    onClick={async () => { try { await navigator.clipboard.writeText(q); toast.success("Copied"); } catch {} }}
                                                    data-testid={`copy-nepq-${i}`}
                                                    className="text-zinc-500 hover:text-white shrink-0"
                                                >
                                                    <Copy className="w-3.5 h-3.5" />
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                            {critique.rewrite_last_message && (
                                <div>
                                    <div className="text-xs font-mono uppercase tracking-widest text-signal mb-2">↻ Rewrite of your last message</div>
                                    <div className="p-3 bg-signal/5 border border-signal/20 rounded-md text-sm text-zinc-100 whitespace-pre-wrap relative" data-testid="critique-rewrite">
                                        {critique.rewrite_last_message}
                                        <button
                                            onClick={async () => { try { await navigator.clipboard.writeText(critique.rewrite_last_message); toast.success("Copied"); } catch {} }}
                                            className="absolute top-2 right-2 text-zinc-500 hover:text-white"
                                        >
                                            <Copy className="w-3.5 h-3.5" />
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
